import React, { useState } from "react";
import { Send, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { formatTime12h, calculateCallTime, getEffectiveStartTime, getTodayString } from "../eventTime";

const fmtDate = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return d; }
};

// Build the Crew Call invitation from the current upcoming Forum events
const buildInvitation = (bodyTemplate, events) => {
  const today = getTodayString();
  const forum = (events || [])
    .filter((e) => (e.location || "").toLowerCase().includes("forum") && e.date && e.date >= today && e.status !== "cancelled")
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  let table;
  if (forum.length === 0) {
    table = "(No upcoming Forum events are currently listed.)";
  } else {
    const first = fmtDate(forum[0].date);
    const last = fmtDate(forum[forum.length - 1].date);
    const header = `UPCOMING FORUM CREW CALL DATES  (${first} – ${last})`;
    const rows = forum.map((e) => {
      const call = formatTime12h(calculateCallTime(e.start_time, e.call_time, e.description || e.notes));
      const show = formatTime12h(getEffectiveStartTime(e.start_time, e.description || e.notes));
      return `• ${fmtDate(e.date)}  |  Crew Call ${call}  |  Show ${show}  |  ${e.name}`;
    });
    table = [header, "", ...rows].join("\n");
  }

  const appLink = `${window.location.origin}/crew/login`;
  const body = (bodyTemplate || "").replace("{events_table}", table).replace("{app_link}", appLink);
  const summary = forum.map((e) => ({ date: e.date, name: e.name, location: e.location }));
  const ids = forum.map((e) => e.id);
  return { body, ids, summary };
};

export const EmailTab = (props) => {
  const { emailForm, sendingEmail, setEmailForm, handleSendBulkEmail, emailTemplates, handleSaveEmailTemplate, events } = props;
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const selectedTemplate = (emailTemplates || []).find((t) => t.id === selectedTemplateId);
  return (
          <TabsContent value="email" data-testid="email-content">
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
              <div className="p-4 border-b border-zinc-800">
                <h2 className="text-xl font-bold text-crew-yellow">EMAIL CREW MEMBERS</h2>
                <p className="text-zinc-400 text-sm">Send bulk emails to crew members based on different criteria</p>
              </div>

              <div className="p-6">
                <form onSubmit={handleSendBulkEmail} className="space-y-6 max-w-2xl">
                  {/* Template selector */}
                  <div className="space-y-2">
                    <Label className="text-zinc-200 font-bold">Load a Template (optional)</Label>
                    <Select
                      value={selectedTemplateId}
                      onValueChange={(id) => {
                        setSelectedTemplateId(id);
                        if (id === "__custom__") {
                          setEmailForm({ ...emailForm, subject: "", body: "", event_ids: null, events_summary: null });
                          return;
                        }
                        const t = (emailTemplates || []).find((x) => x.id === id);
                        if (!t) return;
                        if (t.kind === "crew_invitation") {
                          const { body, ids, summary } = buildInvitation(t.body || "", events);
                          setEmailForm({ ...emailForm, subject: t.subject || "", body, event_ids: ids, events_summary: summary });
                        } else {
                          setEmailForm({ ...emailForm, subject: t.subject || "", body: t.body || "", event_ids: null, events_summary: null });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-black border-zinc-700 text-white" data-testid="email-template-select">
                        <SelectValue placeholder="Choose a saved template..." />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="__custom__" className="text-white hover:bg-zinc-800">— Custom (write your own) —</SelectItem>
                        {(emailTemplates || []).map((t) => (
                          <SelectItem key={t.id} value={t.id} className="text-white hover:bg-zinc-800">{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedTemplate && selectedTemplate.description && (
                      <p className="text-zinc-500 text-xs" data-testid="email-template-description">{selectedTemplate.description}</p>
                    )}
                  </div>

                  {/* Recipient Type */}
                  <div className="space-y-2">
                    <Label className="text-zinc-200 font-bold">Recipients</Label>
                    <Select 
                      value={emailForm.recipient_type} 
                      onValueChange={(value) => setEmailForm({...emailForm, recipient_type: value})}
                    >
                      <SelectTrigger className="bg-black border-zinc-700 text-white" data-testid="email-recipient-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="confirmed_attendees" className="text-white hover:bg-zinc-800">
                          All Confirmed Attendees - Crew confirmed as attending an event
                        </SelectItem>
                        <SelectItem value="all_registrants" className="text-white hover:bg-zinc-800">
                          All Approved Registrants - All approved Crew Call members
                        </SelectItem>
                        <SelectItem value="full_crew" className="text-white hover:bg-zinc-800">
                          Full Crew Call List - All registered crew (approved + pending)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Additional Recipients */}
                  <div className="space-y-2">
                    <Label className="text-zinc-200 font-bold">Additional Recipients (optional)</Label>
                    <Input
                      value={emailForm.additional_recipients}
                      onChange={(e) => setEmailForm({...emailForm, additional_recipients: e.target.value})}
                      placeholder="Enter email addresses separated by commas"
                      className="bg-black border-zinc-700 text-white placeholder:text-zinc-500"
                      data-testid="email-additional-recipients-input"
                    />
                    <p className="text-zinc-500 text-xs">Add extra recipients not in the selected group</p>
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <Label className="text-zinc-200 font-bold">Subject *</Label>
                    <Input
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                      placeholder="Email subject line"
                      required
                      className="bg-black border-zinc-700 text-white placeholder:text-zinc-500"
                      data-testid="email-subject-input"
                    />
                  </div>

                  {/* Body */}
                  <div className="space-y-2">
                    <Label className="text-zinc-200 font-bold">Message *</Label>
                    <Textarea
                      value={emailForm.body}
                      onChange={(e) => setEmailForm({...emailForm, body: e.target.value})}
                      placeholder="Type your message here..."
                      required
                      rows={10}
                      className="bg-black border-zinc-700 text-white placeholder:text-zinc-500"
                      data-testid="email-body-textarea"
                    />
                  </div>

                  {/* Send Button */}
                  <div className="flex gap-4">
                    <Button
                      type="submit"
                      disabled={sendingEmail}
                      className="bg-crew-yellow text-black font-bold hover:bg-white"
                      data-testid="send-email-button"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setEmailForm({...emailForm, subject: '', body: '', additional_recipients: ''}); setSelectedTemplateId(''); }}
                      className="border-zinc-700 text-zinc-300 hover:text-white"
                    >
                      Clear
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!emailForm.subject && !emailForm.body) return;
                        const name = window.prompt('Template name:');
                        if (name && name.trim()) {
                          const description = window.prompt('Short description (optional):') || '';
                          handleSaveEmailTemplate({ name: name.trim(), description, subject: emailForm.subject, body: emailForm.body });
                        }
                      }}
                      className="border-crew-yellow text-crew-yellow hover:bg-crew-yellow hover:text-black"
                      data-testid="save-template-button"
                    >
                      <Save className="mr-2 h-4 w-4" /> Save as Template
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </TabsContent>
  );
};
